import { ArrowLeft, FlaskConical, PanelRightClose } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { SessionEvalRun } from "~/pages/manifold-nextgen/components/conversations/useSessionEvalRuns";
import { useEvalRunStatus } from "~/pages/manifold-nextgen/hooks/useEvalRunStatus";
import { evalRunLabel } from "~/pages/manifold-nextgen/types/eval-run";
import { evalRunStatusDisplay } from "./SessionContextEvalRunDetail";

interface SessionContextEvalRunListProps {
  evalRuns: SessionEvalRun[];
  onOpenEvalRun: (runId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
}

// The full session eval-run list, reached from the single Apps > Eval runs
// entry. Each row shows the case tags and its live status; selecting one opens
// that run's detail.
export const SessionContextEvalRunList = ({
  evalRuns,
  onOpenEvalRun,
  onBack,
  onCollapse,
}: SessionContextEvalRunListProps) => {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label="Back to session context"
          className="size-8"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <span className="truncate text-sm font-medium text-foreground">Eval runs</span>
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onCollapse}
              aria-label="Collapse session context"
              className="ml-auto size-8"
            >
              <PanelRightClose className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Toggle Context</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {evalRuns.length === 0 ? (
          <p className="p-4 text-center text-xs italic text-muted-foreground/80">
            No eval runs yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {evalRuns.map((run) => (
              <EvalRunListRow key={run.runId} evalRun={run} onOpen={onOpenEvalRun} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// One row: polls its own status (independent of any active turn) so the list
// stays live while open, and labels by the case tags recovered from the
// launch_eval_run result.
const EvalRunListRow = ({
  evalRun,
  onOpen,
}: {
  evalRun: SessionEvalRun;
  onOpen: (runId: string) => void;
}) => {
  const { data } = useEvalRunStatus(evalRun.runId);
  const display = evalRunStatusDisplay(data?.status);
  const label = evalRunLabel(evalRun.tags, evalRun.runId);

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(evalRun.runId)}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-accent/50"
      >
        <FlaskConical className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 text-xs font-medium",
            display.isFailed
              ? "text-destructive"
              : display.isCompleted
                ? "text-muted-foreground"
                : "text-primary"
          )}
        >
          {display.isLive && (
            <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden />
          )}
          {display.label}
        </span>
      </button>
    </li>
  );
};
